import type { AuthChangeEvent, Session } from "@supabase/supabase-js";
import { initialDemoWorkspace } from "./demo-data";
import { demoModeEnabled, supabase } from "./supabase";
import type {
  AppSession,
  CreateProjectInput,
  CreateWorkItemInput,
  Project,
  ProjectMember,
  ProjectSnapshot,
  UpdateProfileInput,
  UserProfile,
  WorkItem,
  WorkItemDependency,
  WorkItemLink,
  WorkspaceData,
  ProjectRole,
  WorkStatus,
} from "../types/domain";

export interface WorkspaceRepository {
  mode: "demo" | "supabase";
  getSession(): Promise<AppSession | null>;
  onAuthChange(callback: (session: AppSession | null) => void): () => void;
  signIn(email: string, password: string): Promise<void>;
  signUp(email: string, password: string, displayName: string): Promise<string>;
  signOut(): Promise<void>;
  loadWorkspace(): Promise<WorkspaceData>;
  updateWorkItemStatus(itemId: string, status: WorkStatus, reason: string | null): Promise<void>;
  createProject(input: CreateProjectInput): Promise<void>;
  createWorkItem(input: CreateWorkItemInput): Promise<void>;
  addMemberByHandle(projectId: string, handle: string, role: ProjectRole): Promise<void>;
  updateMemberRole(projectId: string, userId: string, role: ProjectRole): Promise<void>;
  removeMember(projectId: string, userId: string): Promise<void>;
  updateProfile(input: UpdateProfileInput): Promise<void>;
  bootstrapEremosWorkspace(): Promise<void>;
}

const storageKey = "eremos-dashboard-demo-workspace-v1";

function cloneWorkspace(workspace: WorkspaceData): WorkspaceData {
  return structuredClone(workspace);
}

function sessionFromSupabase(session: Session | null): AppSession | null {
  if (!session?.user) return null;
  return {
    userId: session.user.id,
    email: session.user.email ?? "usuario@eremos.local",
  };
}

class DemoRepository implements WorkspaceRepository {
  mode = "demo" as const;
  private workspace: WorkspaceData;

  constructor() {
    const stored = typeof localStorage === "undefined" ? null : localStorage.getItem(storageKey);
    if (stored) {
      try {
        this.workspace = JSON.parse(stored) as WorkspaceData;
      } catch {
        this.workspace = cloneWorkspace(initialDemoWorkspace);
      }
    } else {
      this.workspace = cloneWorkspace(initialDemoWorkspace);
    }
  }

  private persist(): void {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(storageKey, JSON.stringify(this.workspace));
    }
  }

  async getSession(): Promise<AppSession> {
    return { userId: this.workspace.currentProfile.id, email: "demo@eremos.local" };
  }

  onAuthChange(_callback: (session: AppSession | null) => void): () => void {
    return () => undefined;
  }

  async signIn(): Promise<void> {}
  async signUp(): Promise<string> {
    return "Modo demo activo; no se creó una cuenta.";
  }
  async signOut(): Promise<void> {}

  async loadWorkspace(): Promise<WorkspaceData> {
    return cloneWorkspace(this.workspace);
  }

  async updateWorkItemStatus(
    itemId: string,
    status: WorkStatus,
    reason: string | null,
  ): Promise<void> {
    const item = this.workspace.workItems.find((candidate) => candidate.id === itemId);
    if (!item || item.status === status) return;
    const previous = item.status;
    item.status = status;
    item.updatedAt = new Date().toISOString();
    item.acceptedAt = status === "accepted" ? item.updatedAt : null;
    this.workspace.history.unshift({
      id: `demo-history-${crypto.randomUUID()}`,
      projectId: item.projectId,
      workItemId: item.id,
      fromStatus: previous,
      toStatus: status,
      reason,
      changedBy: this.workspace.currentProfile.id,
      changedAt: item.updatedAt,
    });
    this.persist();
  }

  async createProject(input: CreateProjectInput): Promise<void> {
    const id = crypto.randomUUID();
    const timestamp = new Date().toISOString();
    this.workspace.projects.push({
      id,
      ...input,
      status: "active",
      createdBy: this.workspace.currentProfile.id,
      roadmapDisclaimer:
        "Progreso calculado sobre el roadmap conocido actualmente; no representa todo el trabajo futuro.",
      createdAt: timestamp,
      updatedAt: timestamp,
    });
    this.workspace.members.push({
      projectId: id,
      userId: this.workspace.currentProfile.id,
      role: "owner",
      joinedAt: timestamp,
      profile: this.workspace.currentProfile,
    });
    this.persist();
  }

  async createWorkItem(input: CreateWorkItemInput): Promise<void> {
    const timestamp = new Date().toISOString();
    const parent = input.parentId
      ? this.workspace.workItems.find((candidate) => candidate.id === input.parentId)
      : null;
    this.workspace.workItems.push({
      id: crypto.randomUUID(),
      ...input,
      depth: parent ? parent.depth + 1 : 0,
      startedAt: input.status === "pending" || input.status === "planned" ? null : timestamp,
      acceptedAt: input.status === "accepted" ? timestamp : null,
      metadata: {},
      createdAt: timestamp,
      updatedAt: timestamp,
    });
    this.persist();
  }

  async addMemberByHandle(projectId: string, handle: string, role: ProjectRole): Promise<void> {
    const normalized = handle.toLowerCase().trim();
    const existing = this.workspace.members.find(
      (member) => member.projectId === projectId && member.profile.handle === normalized,
    );
    if (existing) throw new Error("Ese miembro ya pertenece al proyecto.");
    const profile: UserProfile = {
      id: `demo-${normalized}`,
      displayName: handle,
      handle: normalized,
      avatarUrl: null,
    };
    this.workspace.members.push({
      projectId,
      userId: profile.id,
      role,
      joinedAt: new Date().toISOString(),
      profile,
    });
    this.persist();
  }

  async updateMemberRole(projectId: string, userId: string, role: ProjectRole): Promise<void> {
    const member = this.workspace.members.find(
      (candidate) => candidate.projectId === projectId && candidate.userId === userId,
    );
    if (member) member.role = role;
    this.persist();
  }

  async removeMember(projectId: string, userId: string): Promise<void> {
    this.workspace.members = this.workspace.members.filter(
      (member) => !(member.projectId === projectId && member.userId === userId),
    );
    this.persist();
  }

  async updateProfile(input: UpdateProfileInput): Promise<void> {
    this.workspace.currentProfile = { ...this.workspace.currentProfile, ...input };
    this.workspace.members = this.workspace.members.map((member) =>
      member.userId === this.workspace.currentProfile.id
        ? { ...member, profile: this.workspace.currentProfile }
        : member,
    );
    this.persist();
  }

  async bootstrapEremosWorkspace(): Promise<void> {
    this.workspace = cloneWorkspace(initialDemoWorkspace);
    this.persist();
  }
}

interface ProjectRow {
  id: string;
  slug: string;
  name: string;
  summary: string;
  vision: string;
  accent: string;
  status: Project["status"];
  created_by: string;
  roadmap_disclaimer: string;
  created_at: string;
  updated_at: string;
}

interface ProfileRow {
  id: string;
  display_name: string;
  handle: string;
  avatar_url: string | null;
}

interface MemberRow {
  project_id: string;
  user_id: string;
  role: ProjectRole;
  joined_at: string;
}

interface WorkItemRow {
  id: string;
  project_id: string;
  parent_id: string | null;
  item_type: WorkItem["itemType"];
  title: string;
  description: string;
  status: WorkStatus;
  weight: number | string;
  sort_order: number;
  depth: number;
  is_current_focus: boolean;
  started_at: string | null;
  target_date: string | null;
  accepted_at: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

interface DependencyRow {
  project_id: string;
  work_item_id: string;
  depends_on_work_item_id: string;
  dependency_type: WorkItemDependency["dependencyType"];
}

interface LinkRow {
  id: string;
  project_id: string;
  work_item_id: string | null;
  milestone_id: string | null;
  link_type: WorkItemLink["linkType"];
  label: string;
  url: string;
  external_id: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

interface MilestoneRow {
  id: string;
  project_id: string;
  title: string;
  description: string;
  status: WorkStatus;
  target_date: string | null;
  completed_at: string | null;
  sort_order: number;
  weight: number | string;
}

interface HistoryRow {
  id: number | string;
  project_id: string;
  work_item_id: string;
  from_status: WorkStatus | null;
  to_status: WorkStatus;
  reason: string | null;
  changed_by: string;
  changed_at: string;
}

interface SnapshotRow {
  id: string;
  project_id: string;
  captured_at: string;
  progress_percent: number | string;
  progress_model_version: number;
  item_count: number;
  status_counts: Partial<Record<WorkStatus, number>> | null;
  phase_progress: Record<string, number> | null;
  known_roadmap_item_count: number;
  metadata: Record<string, unknown> | null;
}

function rows<T>(data: unknown): T[] {
  return Array.isArray(data) ? (data as T[]) : [];
}

function assertNoError(error: { message: string } | null, context: string): void {
  if (error) throw new Error(`${context}: ${error.message}`);
}

class SupabaseRepository implements WorkspaceRepository {
  mode = "supabase" as const;

  private get client() {
    if (!supabase) throw new Error("Supabase no está configurado.");
    return supabase;
  }

  async getSession(): Promise<AppSession | null> {
    const { data, error } = await this.client.auth.getSession();
    assertNoError(error, "No se pudo leer la sesión");
    return sessionFromSupabase(data.session);
  }

  onAuthChange(callback: (session: AppSession | null) => void): () => void {
    const { data } = this.client.auth.onAuthStateChange(
      (_event: AuthChangeEvent, session: Session | null) => callback(sessionFromSupabase(session)),
    );
    return () => data.subscription.unsubscribe();
  }

  async signIn(email: string, password: string): Promise<void> {
    const { error } = await this.client.auth.signInWithPassword({ email, password });
    assertNoError(error, "No se pudo iniciar sesión");
  }

  async signUp(email: string, password: string, displayName: string): Promise<string> {
    const { data, error } = await this.client.auth.signUp({
      email,
      password,
      options: { data: { display_name: displayName } },
    });
    assertNoError(error, "No se pudo crear la cuenta");
    return data.session
      ? "Cuenta creada y sesión iniciada."
      : "Cuenta creada. Revisa el correo para confirmar el acceso.";
  }

  async signOut(): Promise<void> {
    const { error } = await this.client.auth.signOut();
    assertNoError(error, "No se pudo cerrar la sesión");
  }

  async loadWorkspace(): Promise<WorkspaceData> {
    const session = await this.getSession();
    if (!session) throw new Error("La sesión expiró.");

    const [
      projectResult,
      profileResult,
      memberResult,
      itemResult,
      dependencyResult,
      linkResult,
      milestoneResult,
      historyResult,
      snapshotResult,
    ] = await Promise.all([
      this.client.from("projects").select("*").order("created_at"),
      this.client.from("profiles").select("*"),
      this.client.from("project_members").select("*"),
      this.client.from("work_items").select("*").order("sort_order"),
      this.client.from("work_item_dependencies").select("*"),
      this.client.from("work_item_links").select("*").order("created_at", { ascending: false }),
      this.client.from("milestones").select("*").order("sort_order"),
      this.client
        .from("status_history")
        .select("*")
        .order("changed_at", { ascending: false })
        .limit(500),
      this.client.from("project_snapshots").select("*").order("captured_at"),
    ]);

    [
      projectResult,
      profileResult,
      memberResult,
      itemResult,
      dependencyResult,
      linkResult,
      milestoneResult,
      historyResult,
      snapshotResult,
    ].forEach((result, index) => {
      assertNoError(result.error, `No se pudo cargar el recurso ${index + 1}`);
    });

    const profiles = rows<ProfileRow>(profileResult.data).map<UserProfile>((row) => ({
      id: row.id,
      displayName: row.display_name,
      handle: row.handle,
      avatarUrl: row.avatar_url,
    }));
    const profileById = new Map(profiles.map((profile) => [profile.id, profile]));
    const currentProfile = profileById.get(session.userId) ?? {
      id: session.userId,
      displayName: session.email,
      handle: `user-${session.userId.slice(0, 8)}`,
      avatarUrl: null,
    };

    return {
      projects: rows<ProjectRow>(projectResult.data).map((row) => ({
        id: row.id,
        slug: row.slug,
        name: row.name,
        summary: row.summary,
        vision: row.vision,
        accent: row.accent,
        status: row.status,
        createdBy: row.created_by,
        roadmapDisclaimer: row.roadmap_disclaimer,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      })),
      members: rows<MemberRow>(memberResult.data).map<ProjectMember>((row) => ({
        projectId: row.project_id,
        userId: row.user_id,
        role: row.role,
        joinedAt: row.joined_at,
        profile: profileById.get(row.user_id) ?? {
          id: row.user_id,
          displayName: "Miembro",
          handle: row.user_id.slice(0, 8),
          avatarUrl: null,
        },
      })),
      workItems: rows<WorkItemRow>(itemResult.data).map((row) => ({
        id: row.id,
        projectId: row.project_id,
        parentId: row.parent_id,
        itemType: row.item_type,
        title: row.title,
        description: row.description,
        status: row.status,
        weight: Number(row.weight),
        sortOrder: row.sort_order,
        depth: row.depth,
        isCurrentFocus: row.is_current_focus,
        startedAt: row.started_at,
        targetDate: row.target_date,
        acceptedAt: row.accepted_at,
        metadata: row.metadata ?? {},
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      })),
      dependencies: rows<DependencyRow>(dependencyResult.data).map((row) => ({
        projectId: row.project_id,
        workItemId: row.work_item_id,
        dependsOnWorkItemId: row.depends_on_work_item_id,
        dependencyType: row.dependency_type,
      })),
      links: rows<LinkRow>(linkResult.data).map((row) => ({
        id: row.id,
        projectId: row.project_id,
        workItemId: row.work_item_id,
        milestoneId: row.milestone_id,
        linkType: row.link_type,
        label: row.label,
        url: row.url,
        externalId: row.external_id,
        metadata: row.metadata ?? {},
        createdAt: row.created_at,
      })),
      milestones: rows<MilestoneRow>(milestoneResult.data).map((row) => ({
        id: row.id,
        projectId: row.project_id,
        title: row.title,
        description: row.description,
        status: row.status,
        targetDate: row.target_date,
        completedAt: row.completed_at,
        sortOrder: row.sort_order,
        weight: Number(row.weight),
      })),
      history: rows<HistoryRow>(historyResult.data).map((row) => ({
        id: String(row.id),
        projectId: row.project_id,
        workItemId: row.work_item_id,
        fromStatus: row.from_status,
        toStatus: row.to_status,
        reason: row.reason,
        changedBy: row.changed_by,
        changedAt: row.changed_at,
      })),
      snapshots: rows<SnapshotRow>(snapshotResult.data).map<ProjectSnapshot>((row) => ({
        id: row.id,
        projectId: row.project_id,
        capturedAt: row.captured_at,
        progressPercent: Number(row.progress_percent),
        progressModelVersion: row.progress_model_version,
        itemCount: row.item_count,
        statusCounts: row.status_counts ?? {},
        phaseProgress: row.phase_progress ?? {},
        knownRoadmapItemCount: row.known_roadmap_item_count,
        metadata: row.metadata ?? {},
      })),
      currentProfile,
    };
  }

  async updateWorkItemStatus(
    itemId: string,
    status: WorkStatus,
    reason: string | null,
  ): Promise<void> {
    const { data, error } = await this.client.rpc("set_work_item_status", {
      p_work_item_id: itemId,
      p_status: status,
      p_reason: reason,
    });
    assertNoError(error, "No se pudo cambiar el estado");
    if (data === null) throw new Error("La transición no produjo resultado.");
  }

  async createProject(input: CreateProjectInput): Promise<void> {
    const { error } = await this.client.from("projects").insert({
      name: input.name,
      slug: input.slug,
      summary: input.summary,
      vision: input.vision,
      accent: input.accent,
    });
    assertNoError(error, "No se pudo crear el proyecto");
  }

  async createWorkItem(input: CreateWorkItemInput): Promise<void> {
    const { error } = await this.client.from("work_items").insert({
      project_id: input.projectId,
      parent_id: input.parentId,
      item_type: input.itemType,
      title: input.title,
      description: input.description,
      status: input.status,
      weight: input.weight,
      sort_order: input.sortOrder,
      is_current_focus: input.isCurrentFocus,
      target_date: input.targetDate,
    });
    assertNoError(error, "No se pudo crear el elemento");
  }

  async addMemberByHandle(projectId: string, handle: string, role: ProjectRole): Promise<void> {
    const { error } = await this.client.rpc("add_project_member_by_handle", {
      p_project_id: projectId,
      p_handle: handle,
      p_role: role,
    });
    assertNoError(error, "No se pudo añadir el miembro");
  }

  async updateMemberRole(projectId: string, userId: string, role: ProjectRole): Promise<void> {
    const { error } = await this.client
      .from("project_members")
      .update({ role })
      .eq("project_id", projectId)
      .eq("user_id", userId);
    assertNoError(error, "No se pudo cambiar el rol");
  }

  async removeMember(projectId: string, userId: string): Promise<void> {
    const { error } = await this.client
      .from("project_members")
      .delete()
      .eq("project_id", projectId)
      .eq("user_id", userId);
    assertNoError(error, "No se pudo retirar el miembro");
  }

  async updateProfile(input: UpdateProfileInput): Promise<void> {
    const session = await this.getSession();
    if (!session) throw new Error("La sesión expiró.");
    const { error } = await this.client
      .from("profiles")
      .update({
        display_name: input.displayName,
        handle: input.handle,
        avatar_url: input.avatarUrl,
      })
      .eq("id", session.userId);
    assertNoError(error, "No se pudo actualizar el perfil");
  }

  async bootstrapEremosWorkspace(): Promise<void> {
    const { error } = await this.client.rpc("bootstrap_eremos_workspace");
    assertNoError(error, "No se pudo cargar el roadmap inicial");
  }
}

export const workspaceRepository: WorkspaceRepository =
  supabase && !demoModeEnabled ? new SupabaseRepository() : new DemoRepository();
